import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { encryptPassword } from '../utils/rsaEncryption';
import { login, buildLoginRequest, getWeChatOpenIdFromUrl } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import './ModernLogin.css';

interface ModernLoginPageProps {
    onLoginSuccess?: (data: any) => void;
}

const ModernLoginPage: React.FC<ModernLoginPageProps> = ({ onLoginSuccess }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [weChatOpenId, setWeChatOpenId] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const { login: wechatLogin, refreshUserInfo, isLoggedIn, isLoading: authLoading } = useAuth();

    useEffect(() => {
        // 从URL获取微信OpenId
        const openId = getWeChatOpenIdFromUrl();
        setWeChatOpenId(openId);

        // 添加body类名防止滚动
        document.body.classList.add('login-page');
        document.documentElement.classList.add('login-page');

        // 清理函数
        return () => {
            document.body.classList.remove('login-page');
            document.documentElement.classList.remove('login-page');
        };
    }, []); // 移除location依赖，只在组件挂载时执行一次

    // 如果用户已登录，跳转到首页
    useEffect(() => {
        // 只有在认证完成且确实登录成功时才跳转
        if (isLoggedIn && !authLoading) {
            console.log('用户已登录，准备跳转');

            // 使用当前URL的search参数，避免依赖location对象
            const urlParams = new URLSearchParams(window.location.search);
            const redirectUrl = urlParams.get('redirect');
            const targetUrl = redirectUrl ? decodeURIComponent(redirectUrl) : '/';

            console.log('跳转目标:', targetUrl);

            // 延迟跳转，确保状态稳定
            setTimeout(() => {
                window.location.href = targetUrl;
            }, 100);
        } else if (!authLoading && !isLoggedIn && weChatOpenId) {
            console.log('微信登录失败，用户需要手动绑定账号');
        }
    }, [isLoggedIn, authLoading, weChatOpenId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            if (!username.trim()) {
                throw new Error('请输入账号');
            }
            if (!password.trim()) {
                throw new Error('请输入密码');
            }
            if (!weChatOpenId) {
                throw new Error('未获取到微信授权信息，请重新授权');
            }

            // 加密密码
            const encryptedPassword = encryptPassword(password);
            if (!encryptedPassword) {
                throw new Error('密码加密失败，请稍后重试');
            }

            // 构建登录请求
            const loginRequest = buildLoginRequest(
                username.trim(),
                encryptedPassword,
                true, // 默认记住登录状态
                weChatOpenId
            );

            // 发送登录请求
            const response = await login(loginRequest);

            if (response.success) {
                console.log('账号绑定成功:', response.data);

                // 账号绑定成功后，尝试微信登录
                console.log('开始微信登录...');
                const wechatLoginSuccess = await wechatLogin();

                if (wechatLoginSuccess) {
                    console.log('微信登录成功，准备跳转');

                    // 刷新用户信息
                    await refreshUserInfo();

                    if (onLoginSuccess) {
                        onLoginSuccess(response.data);
                    }

                    // 获取重定向URL
                    const urlParams = new URLSearchParams(location.search);
                    const redirectUrl = urlParams.get('redirect');
                    const targetUrl = redirectUrl ? decodeURIComponent(redirectUrl) : '/';

                    console.log('准备跳转到:', targetUrl);
                    navigate(targetUrl, { replace: true });
                } else {
                    throw new Error('账号绑定成功，但微信登录失败，请刷新页面重试');
                }
            } else {
                throw new Error(response.message || '登录失败');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : '登录失败，请稍后重试');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="h-screen bg-gray-100 flex flex-col modern-login-container modern-login-safe-area overflow-hidden">
            {/* 状态栏区域 */}
            <div className="flex-shrink-0 h-8 bg-gray-100"></div>



            {/* Logo 和标题区域 */}
            <div className="flex-shrink-0 px-6 pt-8 pb-4">
                <div className="flex items-center mb-6">
                    <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center mr-3">
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                    </div>
                    <span className="text-lg font-semibold text-gray-900">锦湖日丽</span>
                </div>

                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    欢迎回来！
                </h1>
                <p className="text-gray-600 text-base">
                    首次使用请绑定账户
                </p>
            </div>

            {/* 表单区域 */}
            <div className="flex-1 px-6 flex flex-col justify-start pt-4">
                <form onSubmit={handleSubmit} className="space-y-5">

                    {/* 错误提示 */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4 error-slide-in">
                            <p className="text-red-600 text-sm font-medium">{error}</p>
                        </div>
                    )}

                    {/* 账号输入 */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            账号
                        </label>
                        <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="w-full px-4 py-4 text-base border-0 rounded-3xl bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-0 focus:bg-white transition-all duration-200 modern-login-input"
                            placeholder="请输入账号"
                            required
                        />
                    </div>

                    {/* 密码输入 */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            密码
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-4 pr-12 text-base border-0 rounded-3xl bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-0 focus:bg-white transition-all duration-200 modern-login-input"
                                placeholder="请输入密码"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute inset-y-0 right-0 pr-4 flex items-center"
                            >
                                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    {showPassword ? (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                                    ) : (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    )}
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* 注册为新供应商 */}
                    <div className="text-right">
                        <button
                            type="button"
                            onClick={() => navigate('/register')}
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        >
                            注册为新供应商
                        </button>
                    </div>

                    {/* 登录按钮 */}
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-blue-600 text-white py-4 px-4 rounded-3xl text-base font-semibold hover:bg-blue-700 focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 modern-login-button"
                    >
                        {isLoading ? (
                            <div className="flex items-center justify-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                登录中...
                            </div>
                        ) : (
                            '登录'
                        )}
                    </button>


                </form>


            </div>

            {/* 微信信息显示（调试用） */}
            {weChatOpenId && (
                <div className="fixed bottom-4 left-4 right-4 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
                    微信授权: {weChatOpenId.substring(0, 10)}...
                </div>
            )}
        </div>
    );
};

export default ModernLoginPage;
